import React, { Fragment, useContext, useEffect, useState } from 'react';
import capitalize from 'lodash/capitalize';
import filter from 'lodash/filter';
import find from 'lodash/find';
import get from 'lodash/get';
import groupBy from 'lodash/groupBy';
import sortBy from 'lodash/sortBy';
import startsWith from 'lodash/startsWith';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { withRouter } from 'react-router-dom';
import Cookies from 'js-cookie';

import { GridContainer } from 'formlabs-web-components/Grid';
import { Spinner } from 'formlabs-web-components/Navigation';

import CountryContext from '../../../../common/context/country';
import clientOnly from '../../../../common/hoc/clientOnly';
import { updateLocale as updateLocaleAction } from '../../../../common/ducks/i18n';
import { migrateCartToStore as migrateCartToStoreAction } from '../../../../common/ducks/cart';
import { getCookieDomain } from '../../../../common/utils';

import './RegionBar.module.css';

const renderOption = ({ code, name, languageName }) => (
  <option key={code} value={code}>{`${name} (${capitalize(
    languageName.split(',')[0]
  )})`}</option>
);

const fixStoreId = (region, language, callback = null) => {
  const desiredStoreId = `${region}_${language}`;
  const storeId = Cookies.get('store_view');
  if (storeId && desiredStoreId !== storeId) {
    Cookies.remove('store_view', { domain: getCookieDomain() });
    Cookies.remove('store_path', { domain: getCookieDomain() });
    if (callback) {
      callback(region);
    }
  }
};

const RegionBar = ({
  activeRegion,
  countries,
  detectedLocale,
  history,
  location: { pathname },
  updateLocale,
  allowedCountries,
  pageLanguage,
  pageRegion,
  migrateCartToStore,
  cartWasMigrated
}) => {
  const { t: translate, i18n } = useTranslation();
  // Allow for patching the t func for target language
  const [t, setTranslateFunction] = useState(() => translate);

  const detectedRegionCode = get(detectedLocale, 'region.code', null);
  const [hide, setHide] = useState(!!Cookies.get('hide-region-bar') || false);
  const [selectedRegion, setRegion] = useState(null);
  const { storePage } = useContext(CountryContext);

  const [loading, setLoading] = useState(false);
  const [afterMigrationCallback, setAfterMigrationCallback] = useState(null);

  const isStorePage = startsWith(pathname, storePage);
  const validRegionCodes = allowedCountries.map(region =>
    region.iso.toLowerCase()
  );

  // dont show when geo region match or bar was dismissed (but override on store pages)
  const hideRegionBar =
    (hide && !isStorePage) ||
    (detectedRegionCode &&
      detectedRegionCode.toLowerCase() === activeRegion &&
      validRegionCodes.includes(activeRegion)) ||
    (detectedRegionCode && !find(countries, { code: detectedRegionCode })) ||
    detectedRegionCode === null;

  // just use the two char iso codes, we could optimize by also respecting the locale part
  const detectedLanguages = filter(
    get(detectedLocale, 'languages', []),
    language => language.length === 2
  );

  // preselect possible regions by detected geo region
  let regions = detectedRegionCode
    ? groupBy(countries, country => country.code === detectedRegionCode)
    : { false: countries };

  const matchedRegions = regions.true;

  // preselect possible regions by detected browser languages
  if (!matchedRegions && detectedLanguages.length) {
    const candidates = groupBy(countries, country =>
      detectedLanguages.includes(country.language)
    );
    if (candidates.true) {
      regions = candidates;
    }
  }

  // use first match as initial preselect or disable button
  const preSelectedRegion = get(regions, 'true[0]', null);

  useEffect(
    () => {
      if (cartWasMigrated && afterMigrationCallback) {
        afterMigrationCallback();
      }
    },
    [cartWasMigrated]
  );

  // Patch the local translate function to detected locale
  useEffect(() => {
    const switchLocale = async targetLanguage => {
      if (!i18n.hasResourceBundle(targetLanguage, 'common')) {
        const localeImport = await import(/* webpackChunkName: "locale-[request]" */ `../../../../common/i18n/locales/${targetLanguage}.js`);
        i18n.addResourceBundle(targetLanguage, 'common', localeImport.messages);
      }
      const patchedTranslate = i18n.getFixedT(targetLanguage, 'common');
      setTranslateFunction(() => patchedTranslate);
    };

    const targetLanguage = preSelectedRegion
      ? preSelectedRegion.language
      : null;
    if (!hideRegionBar && i18n.language !== targetLanguage) {
      switchLocale(targetLanguage);
    }

    // make sure to switch locale to active store page
    if (isStorePage && !validRegionCodes.includes(activeRegion)) {
      fixStoreId(pageRegion, pageLanguage);
      updateLocale(
        pageLanguage,
        pageRegion,
        find(countries, { code: pageRegion.toUpperCase() }).currencyCode
      );

      window.location.reload(true);
    }
  }, []);

  useEffect(
    () => {
      const cleanDetectedRegionCode = (detectedRegionCode || '').toLowerCase();
      // O Canada (https://formlabs.atlassian.net/browse/FL-1693)
      if (
        isStorePage &&
        cleanDetectedRegionCode &&
        cleanDetectedRegionCode === 'ca' &&
        cleanDetectedRegionCode !== activeRegion
      ) {
        const canada = find(countries, { code: 'CA' });
        fixStoreId('ca', 'en');
        updateLocale('en', 'ca', canada.currencyCode);
        if (!pathname.startsWith(canada.storePage)) {
          history.push(canada.storePage, { forceUpdate: true });
        }
      }
    },
    [detectedRegionCode]
  );

  if (hideRegionBar) {
    return null;
  }

  const furtherRegions =
    regions.false && regions.false.length
      ? sortBy(regions.false, ['name', 'languageName'])
      : null;

  const handleDismiss = event => {
    event.preventDefault();
    Cookies.set('hide-region-bar', true);
    setHide(true);
  };

  const handleSubmit = () => {
    const { language, path, code, currencyCode, storePage } =
      selectedRegion || preSelectedRegion;
    const region = code.toLowerCase();

    setLoading(true);

    fixStoreId(region, language, region => {
      migrateCartToStore(region);
      setAfterMigrationCallback(() => () => {
        updateLocale(language, region, currencyCode);
        history.push(isStorePage && storePage ? storePage : path, {
          forceUpdate: true
        });
      });
    });
  };

  return (
    <div styleName="bar">
      <GridContainer>
        <div styleName="inner">
          <div styleName="label">
            {t(
              'Please select a country or region to customize the content for your location.'
            )}
          </div>
          <div styleName="form">
            <div styleName="select">
              <select
                onBlur={event => {
                  setRegion(find(countries, { code: event.target.value }));
                }}
                aria-label={t('Region selector')}
              >
                {matchedRegions ? (
                  <Fragment>
                    {matchedRegions.map(country => renderOption(country))}
                    <option disabled>--------------------</option>
                  </Fragment>
                ) : null}
                {furtherRegions
                  ? furtherRegions.map(country => renderOption(country))
                  : null}
              </select>
            </div>
            {loading && <Spinner size={3} color="#0762c8" />}
            <button
              styleName="submit"
              disabled={(!selectedRegion && !preSelectedRegion) || loading}
              onClick={handleSubmit}
            >
              {t('Continue')}
            </button>
          </div>
        </div>
      </GridContainer>
      {!isStorePage ? (
        <button
          styleName="dismiss"
          title={t('Do not ask me again.')}
          onClick={handleDismiss}
        >
          &#215;
        </button>
      ) : null}
    </div>
  );
};

export default compose(
  connect(
    state => {
      return {
        activeLanguage: state.i18n.language,
        activeRegion: state.i18n.region,
        detectedLocale: state.i18n.detectedLocale,
        cartWasMigrated: state.cart.wasMigrated
      };
    },
    dispatch => {
      return {
        updateLocale: (language, region, currency) => {
          dispatch(updateLocaleAction(language, region, currency));
        },
        migrateCartToStore: storeId => {
          dispatch(migrateCartToStoreAction(storeId));
        }
      };
    }
  ),
  withRouter,
  clientOnly
)(RegionBar);
